import React from 'react';

import { BuilderComponent, Builder } from '@builder.io/react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import { trackBuilderTest } from '~/builder/utils/builderTesting';
import { getBuilderJsonSSR } from '~/builder/utils/getBuilderJsonSSR';
import {
  allComponents,
  getComponentNamesForMenu,
  gridOnlyComponents,
  registerBuilderComponents,
} from '~/builder/utils/registerBuilderComponents';
import { useBuilderContent } from '~/builder/utils/useBuilderContent';
import useBuilderJson from '~/builder/utils/useBuilderJson';
import DefaultLayout from '~/layout/components/DefaultLayout';
import { useBrandDomainCanonicalLink } from '~/shared/utils/useBrandDomainCanonicalLink';

import createPage from '../../registry';
import NotFoundPage from '../404';

function BuilderCategoryPage({
  builderJson,
  builderJsonKey,
  builderStateData,
}) {
  const canonicalPath = useBrandDomainCanonicalLink();
  const router = useRouter();
  const { asPath } = router;
  const pathname = asPath?.split('?')[0];
  const { builderJson: clientBuilderJson, isLoading } = useBuilderJson({
    modelName: 'category-page',
    urlPath: pathname,
    ssrBuilderJson: builderJson,
    ssrBuilderJsonKey: builderJsonKey,
  });
  const builderJsonToRender = clientBuilderJson || builderJson;
  const { content, locale } = useBuilderContent(builderJsonToRender);
  const dispatch = useDispatch();

  const builderContentLoaded =
    (!isLoading && content) || Builder.isPreviewing || Builder.isEditing;
  const pageNotFound =
    !isLoading && !content && !(Builder.isPreviewing || Builder.isEditing);

  return (
    <DefaultLayout
      title={content?.data?.title}
      description={content?.data?.title}
      canonicalPath={canonicalPath}
    >
      {pageNotFound && <NotFoundPage />}
      {builderContentLoaded && (
        <BuilderComponent
          model="category-page"
          content={content}
          locale={locale}
          data={builderStateData}
          options={{
            enrich: true,
          }}
          contentLoaded={(data, content) => {
            trackBuilderTest(dispatch, content);
          }}
        />
      )}
    </DefaultLayout>
  );
}

BuilderCategoryPage.getInitialProps = async (ctx) => {
  const pathname = ctx.asPath?.split('?') ? ctx.asPath?.split('?')[0] : null;

  const {
    builderJson,
    builderStateData,
    key: builderJsonKey,
  } = await getBuilderJsonSSR({
    modelName: 'category-page',
    urlPath: pathname,
    ctx,
  });

  return {
    builderJson,
    builderStateData,
    builderJsonKey,
  };
};

BuilderCategoryPage.propTypes = {
  builderJson: PropTypes.object,
  builderJsonKey: PropTypes.string,
  builderStateData: PropTypes.object,
};

registerBuilderComponents(allComponents);
registerBuilderComponents(gridOnlyComponents);
Builder.register('insertMenu', {
  name: 'FL - Grid Components',
  items: getComponentNamesForMenu(gridOnlyComponents),
});

export default createPage(BuilderCategoryPage, {
  resourceBundles: [
    'app_grid',
    'grid',
    'site_home',
    'site_product_category',
    'site_product_filter',
    'site_product_grid',
    'site_home_boutique',
    'top_sellers_listing',
  ],
});
